import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { cleanText } from '../utils/textUtils';
import FavoriteButton from '../components/FavoriteButton';
import ShareButton from '../components/ShareButton';
import VenueHours from '../components/VenueHours';
import './VenueDetail.css';

interface Venue {
  id: string;
  slug: string;
  name: string;
  description?: string;
  short_description: string;
  venue_type: string;
  address: string;
  city: string;
  state: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  capacity_min?: number;
  capacity_max?: number;
  price_range_min?: number;
  price_range_max?: number;
  price_description?: string;
  activities?: string[];
  indoor: boolean;
  catering_included: boolean;
  party_host_included: boolean;
  website_url?: string;
  booking_url?: string;
}

const VenueDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) fetchVenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchVenue = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from('venues')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'active')
        .single();
      
      if (error) throw error;
      if (!data) throw new Error('Venue not found');
      setVenue(data);
    } catch (err: any) {
      console.error('Error fetching venue:', err);
      setError(err.message || 'Failed to load venue details');
    } finally {
      setLoading(false);
    }
  };

  const extractRating = (description: string): { rating: string; reviews: string } | null => {
    const match = description.match(/Rated ([\d.]+) stars \((\d+) reviews?\)/);
    if (match) return { rating: match[1], reviews: match[2] };
    return null;
  };

  const generateDescription = (venue: Venue): string => {
    const name = venue.name.toLowerCase();
    const venueTypeLower = (venue.venue_type || '').toLowerCase();
    
    // Extract venue type from name or venue_type field
    let description = '';
    
    // Check name keywords first
    if (name.includes('mini golf') || name.includes('putt')) {
      description = 'Enjoy mini golf and family fun at this exciting venue';
    } else if (name.includes('arcade') || name.includes('game')) {
      description = 'Arcade games and entertainment for all ages';
    } else if (name.includes('trampoline') || name.includes('jump')) {
      description = 'High-energy trampoline park with jumping zones and activities';
    } else if (name.includes('bowling')) {
      description = 'Bowling lanes with party packages and food options';
    } else if (name.includes('laser') || name.includes('tag')) {
      description = 'Laser tag arena with exciting team missions';
    } else if (name.includes('skate') || name.includes('roller')) {
      description = 'Skating rink with music, lights, and party areas';
    } else if (name.includes('museum')) {
      description = 'Interactive museum with hands-on exhibits';
    } else if (name.includes('playground') || name.includes('play')) {
      description = 'Indoor playground with safe play areas for kids';
    } else if (name.includes('swim') || name.includes('aqua')) {
      description = 'Swimming facility offering party packages and pool fun';
    } else if (name.includes('martial') || name.includes('karate')) {
      description = 'Martial arts facility with party programs';
    } else if (name.includes('escape')) {
      description = 'Escape room adventures with challenging puzzles';
    } else if (name.includes('axe')) {
      description = 'Axe throwing venue with guided sessions';
    } 
    // Check venue type if no name match
    else if (venueTypeLower.includes('attraction')) {
      description = 'Entertainment attraction perfect for celebrations';
    } else if (venueTypeLower.includes('activity')) {
      description = 'Activity center with organized programs and events';
    } 
    // Default based on what we know
    else if (venue.indoor) {
      description = 'Indoor event space perfect for birthday celebrations';
    } else {
      description = 'Private event venue with party hosting services';
    }
    
    const capacity = venue.capacity_min && venue.capacity_max 
      ? `Accommodates ${venue.capacity_min}-${venue.capacity_max} guests`
      : 'Flexible capacity for various group sizes';
    
    return `${description}. ${capacity}. Contact them for party packages and booking details.`;
  };

  if (loading) {
    return (
      <div className="venue-detail-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading venue details...</p>
        </div>
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div className="venue-detail-container">
        <div className="error">
          <h2>😕 Venue Not Found</h2>
          <p>{error || 'This venue could not be found.'}</p>
          <button onClick={() => navigate('/birthday-parties')} className="back-btn">
            ← Back to Birthday Venues
          </button>
        </div>
      </div>
    );
  }

  const rating = extractRating(venue.short_description);

  return (
    <div className="venue-detail-container">
      <button onClick={() => navigate('/birthday-parties')} className="back-button">
        ← Back to Birthday Venues
      </button>
      
      <div className="venue-detail-content">
        <div className="venue-header">
          <div className="venue-action-buttons">
            <FavoriteButton itemId={venue.id} itemType="venue" size="large" />
            <ShareButton 
              title={venue.name}
              description={generateDescription(venue)}
              url={`${window.location.origin}/venue/${venue.slug}`}
              location={`${venue.city}, ${venue.state}`}
              size="large"
            />
          </div>
          
          <div className="venue-title-section">
            <h1 className="venue-title">{venue.name}</h1>
            <p className="venue-type-badge">{venue.venue_type}</p>
            {rating && (
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.name + ' ' + venue.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="venue-rating-large"
                title="View on Google Maps"
              >
                <span className="stars">⭐</span>
                <span className="rating-value">{rating.rating}</span>
                <span className="rating-count">({rating.reviews} reviews)</span>
              </a>
            )}
          </div>
        </div>

        <div className="venue-body">
          <div className="info-section">
            <h2 className="section-title">📝 About This Venue</h2>
            <div className="info-content">
              <p className="description">{cleanText(generateDescription(venue))}</p>
            </div>
          </div>

          <div className="info-section">
            <h2 className="section-title">📍 Location</h2>
            <div className="info-content">
              <p className="venue-name-display">{venue.name}</p>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  `${venue.name} ${venue.address}, ${venue.city}, ${venue.state}`.trim()
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="location-link address-link"
              >
                {venue.address}, {venue.city}, {venue.state} {venue.zip_code || ''}
              </a>
            </div>
          </div>

          <div className="info-section">
            <h2 className="section-title">👥 Capacity & Pricing</h2>
            <div className="info-content">
              {venue.capacity_min && venue.capacity_max && (
                <p><strong>Capacity:</strong> {venue.capacity_min}-{venue.capacity_max} guests</p>
              )}
              {venue.price_range_max && (
                <p><strong>Price Range:</strong> Up to ${venue.price_range_max}</p>
              )}
              {venue.price_description && (
                <p className="price-note">{venue.price_description}</p>
              )}
            </div>
          </div>

          <div className="info-section">
            <h2 className="section-title">🎉 Features</h2>
            <div className="info-content amenities-list">
              {venue.indoor && <div className="amenity">✓ Indoor venue</div>}
              {venue.catering_included && <div className="amenity">✓ Catering available</div>}
              {venue.party_host_included && <div className="amenity">✓ Party host included</div>}
              {venue.activities && venue.activities.length > 0 && (
                <div className="activities-section">
                  <strong>Activities:</strong> {venue.activities.join(', ')}
                </div>
              )}
            </div>
          </div>

          {venue.description && (
            <div className="info-section full-width">
              <h2 className="section-title">🕐 Hours</h2>
              <VenueHours description={venue.description} />
            </div>
          )}

          {(venue.website_url || venue.booking_url) && (
            <div className="info-section full-width">
              <h2 className="section-title">🔗 Links</h2>
              <div className="info-content icon-actions">
                {venue.website_url && (
                  <button 
                    className="icon-link-button"
                    onClick={() => window.open(venue.website_url, '_blank')}
                    title="Visit Website"
                    aria-label="Visit Website"
                  >
                    🌐
                  </button>
                )}
                {venue.booking_url && (
                  <button 
                    className="icon-link-button"
                    onClick={() => window.open(venue.booking_url, '_blank')}
                    title="Book a Party"
                    aria-label="Book a Party"
                  >
                    🎈
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VenueDetail;
